// src/pages/customer/MyAccount.jsx
import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useForm } from "react-hook-form";
import { updateProfile } from "firebase/auth";
import { auth } from "../../services/firebase";
import { useAuth } from "../../context/AuthContext";
import {
  getUserProfile, updateUserProfile,
  addUserAddress, updateUserAddress, deleteUserAddress, setDefaultUserAddress,
} from "../../services/firestoreService";
import Spinner from "../../components/common/Spinner";
import toast from "react-hot-toast";
import { FiUser, FiMail, FiPhone, FiLock, FiMapPin, FiPlus, FiEdit2, FiTrash2 } from "react-icons/fi";

export default function MyAccount() {
  const { user, refreshProfile } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Full profile (incl. saved addresses) kept locally so the address list
  // below can be re-rendered right after an add/edit/delete without a
  // page reload. useAuth's userProfile is used elsewhere for header etc.,
  // but this screen needs its own copy to refresh on demand.
  const [profile, setProfile] = useState(null);

  const { register, handleSubmit, reset, formState: { errors } } = useForm();

  useEffect(() => {
    if (!user) return;
    getUserProfile(user.uid).then((p) => {
      setProfile(p);
      reset({
        name: p?.name || user.displayName || "",
        phone: p?.phone || "",
        email: user.email || "",
      });
      setLoading(false);
    });
  }, [user, reset]);

  const onSubmit = async (data) => {
    setSaving(true);
    try {
      await updateUserProfile(user.uid, {
        name: data.name,
        phone: data.phone,
      });
      // Keep Firebase Auth's displayName in sync too, since some parts of
      // the app (and Firebase itself) read the name from there.
      await updateProfile(auth.currentUser, { displayName: data.name });
      // Update AuthContext's in-memory profile immediately so the header
      // dropdown (and anywhere else using userProfile) reflects the new
      // name right away, without needing a reload or re-login.
      await refreshProfile();
      toast.success("Account details updated");
    } catch {
      toast.error("Update failed. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  // ── Saved addresses ──────────────────────────────────────────────────────
  const [editingAddressId, setEditingAddressId] = useState(null); // null | "new" | address id
  const [savingAddress, setSavingAddress] = useState(false);
  const addrForm = useForm({
    defaultValues: { label: "", address: "", city: "", state: "", pincode: "", landmark: "" },
  });

  const refreshLocalProfile = async () => {
    const fresh = await getUserProfile(user.uid);
    setProfile(fresh);
    // Also sync AuthContext so the address chosen at checkout stays current
    // if the user places an order right after editing addresses here.
    await refreshProfile();
  };

  const openAddForm = () => {
    addrForm.reset({ label: "", address: "", city: "", state: "", pincode: "", landmark: "" });
    setEditingAddressId("new");
  };

  const openEditForm = (addr) => {
    addrForm.reset({
      label: addr.label || "",
      address: addr.address || "",
      city: addr.city || "",
      state: addr.state || "",
      pincode: addr.pincode || "",
      landmark: addr.landmark || "",
    });
    setEditingAddressId(addr.id);
  };

  const onSaveAddress = async (data) => {
    setSavingAddress(true);
    try {
      if (editingAddressId === "new") {
        await addUserAddress(user.uid, data);
        toast.success("Address added");
      } else {
        await updateUserAddress(user.uid, editingAddressId, data);
        toast.success("Address updated");
      }
      await refreshLocalProfile();
      setEditingAddressId(null);
    } catch {
      toast.error("Could not save address. Please try again.");
    } finally {
      setSavingAddress(false);
    }
  };

  const onDeleteAddress = async (id) => {
    if (!window.confirm("Delete this address?")) return;
    try {
      await deleteUserAddress(user.uid, id);
      await refreshLocalProfile();
      toast.success("Address removed");
    } catch {
      toast.error("Could not delete address. Please try again.");
    }
  };

  const onSetDefaultAddress = async (id) => {
    try {
      await setDefaultUserAddress(user.uid, id);
      await refreshLocalProfile();
    } catch {
      toast.error("Could not update default address.");
    }
  };

  if (loading) return <Spinner center />;

  const addresses = profile?.addresses || [];

  return (
    <div className="min-h-screen" style={{ background: "#f7f3e3" }}>
      <Helmet>
        <title>My Account | Tripura Agro</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">

        {/* ── Header ── */}
        <div className="flex items-center gap-3 mb-6">
          <div className="w-11 h-11 rounded-2xl bg-white border flex items-center justify-center flex-shrink-0" style={{ borderColor: "#e2dcc8" }}>
            <FiUser className="w-5 h-5" style={{ color: "#3a5c2f" }} />
          </div>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold" style={{ color: "#1a3d1a" }}>My Account</h1>
            <p className="text-sm text-gray-500 mt-0.5">View and update your account details</p>
          </div>
        </div>

        {/* ── Profile form card ── */}
        <div className="rounded-2xl border p-5 sm:p-7" style={{ borderColor: "#e2dcc8", background: "#fdfcf6" }}>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">

            <div>
              <label className="label">Full Name</label>
              <div className="relative">
                <FiUser className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
                <input
                  {...register("name", { required: "Full name is required" })}
                  className={`input bg-white pl-9 ${errors.name ? "border-red-400" : ""}`}
                  placeholder="Enter your full name"
                />
              </div>
              {errors.name && <p className="text-xs text-red-500 mt-1">{errors.name.message}</p>}
            </div>

            <div>
              <label className="label">Phone Number</label>
              <div className="relative">
                <FiPhone className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
                <input
                  {...register("phone", {
                    required: "Phone number is required",
                    pattern: { value: /^[0-9]{10}$/, message: "Enter a valid 10-digit number" },
                  })}
                  className={`input bg-white pl-9 ${errors.phone ? "border-red-400" : ""}`}
                  placeholder="+91 98765 43210"
                />
              </div>
              {errors.phone && <p className="text-xs text-red-500 mt-1">{errors.phone.message}</p>}
            </div>

            <div>
              <label className="label">Email Address</label>
              <div className="relative">
                <FiMail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
                <input
                  {...register("email")}
                  disabled
                  className="input bg-gray-100 text-gray-500 pl-9 cursor-not-allowed"
                />
                <FiLock className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-300 w-3.5 h-3.5 pointer-events-none" />
              </div>
              <p className="text-xs text-gray-400 mt-1">Email address cannot be changed.</p>
            </div>

            <button
              type="submit"
              disabled={saving}
              className="btn-primary w-full sm:w-auto flex items-center justify-center gap-2 uppercase tracking-wide text-sm mt-2"
            >
              {saving ? <><Spinner size="sm" /> Saving...</> : "Save Changes"}
            </button>
          </form>
        </div>

        {/* ── Saved addresses card ── */}
        <div className="rounded-2xl border p-5 sm:p-7 mt-6" style={{ borderColor: "#e2dcc8", background: "#fdfcf6" }}>
          <div className="flex items-center justify-between gap-3 mb-4">
            <div className="flex items-center gap-2">
              <FiMapPin className="w-4 h-4" style={{ color: "#3a5c2f" }} />
              <h2 className="font-bold text-base sm:text-lg" style={{ color: "#1a3d1a" }}>Saved Addresses</h2>
            </div>
            {editingAddressId === null && (
              <button
                type="button"
                onClick={openAddForm}
                className="text-sm font-semibold text-primary-600 hover:text-primary-700 flex items-center gap-1"
              >
                <FiPlus className="w-4 h-4" /> Add Address
              </button>
            )}
          </div>

          {addresses.length === 0 && editingAddressId === null && (
            <p className="text-sm text-gray-400">
              You haven't saved any addresses yet. Add one so it's ready to pick at checkout.
            </p>
          )}

          <div className="space-y-3">
            {addresses.map((addr) => (
              editingAddressId === addr.id ? (
                <AddressForm
                  key={addr.id}
                  form={addrForm}
                  onSubmit={onSaveAddress}
                  onCancel={() => setEditingAddressId(null)}
                  saving={savingAddress}
                />
              ) : (
                <div
                  key={addr.id}
                  className="p-4 rounded-xl border flex items-start justify-between gap-3"
                  style={{ borderColor: "#e2dcc8" }}
                >
                  <div className="text-sm min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-gray-900">{addr.label || "Address"}</span>
                      {addr.isDefault && (
                        <span className="text-[10px] uppercase tracking-wide font-bold bg-primary-100 text-primary-700 px-1.5 py-0.5 rounded">
                          Default
                        </span>
                      )}
                    </div>
                    <p className="text-gray-500 mt-0.5">
                      {addr.address}, {addr.city}, {addr.state} - {addr.pincode}
                      {addr.landmark ? ` (${addr.landmark})` : ""}
                    </p>
                  </div>
                  <div className="flex flex-col items-end gap-2 flex-shrink-0 text-xs">
                    <button
                      type="button"
                      onClick={() => openEditForm(addr)}
                      className="text-primary-600 font-semibold flex items-center gap-1 hover:text-primary-700"
                    >
                      <FiEdit2 className="w-3 h-3" /> Edit
                    </button>
                    {!addr.isDefault && (
                      <button
                        type="button"
                        onClick={() => onSetDefaultAddress(addr.id)}
                        className="text-gray-500 hover:text-gray-700"
                      >
                        Set as default
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => onDeleteAddress(addr.id)}
                      className="text-red-500 font-semibold flex items-center gap-1 hover:text-red-600"
                    >
                      <FiTrash2 className="w-3 h-3" /> Delete
                    </button>
                  </div>
                </div>
              )
            ))}

            {editingAddressId === "new" && (
              <AddressForm
                form={addrForm}
                onSubmit={onSaveAddress}
                onCancel={() => setEditingAddressId(null)}
                saving={savingAddress}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// Add/edit form for a single saved address — shared by both flows so the
// fields and validation only need to be defined once.
function AddressForm({ form, onSubmit, onCancel, saving }) {
  const { register, handleSubmit, formState: { errors } } = form;
  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="p-4 rounded-xl border space-y-3"
      style={{ borderColor: "#e2dcc8", background: "#fff" }}
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="sm:col-span-2">
          <label className="label">Label (optional)</label>
          <input {...register("label")} className="input bg-white" placeholder="Home, Office, etc." />
        </div>
        <div className="sm:col-span-2">
          <label className="label">Street Address *</label>
          <input
            {...register("address", { required: "Address is required" })}
            className={`input bg-white ${errors.address ? "border-red-400" : ""}`}
            placeholder="House no, Street name"
          />
          {errors.address && <p className="text-xs text-red-500 mt-1">{errors.address.message}</p>}
        </div>
        <div>
          <label className="label">City *</label>
          <input
            {...register("city", { required: "City is required" })}
            className={`input bg-white ${errors.city ? "border-red-400" : ""}`}
            placeholder="Mumbai"
          />
          {errors.city && <p className="text-xs text-red-500 mt-1">{errors.city.message}</p>}
        </div>
        <div>
          <label className="label">State *</label>
          <input
            {...register("state", { required: "State is required" })}
            className={`input bg-white ${errors.state ? "border-red-400" : ""}`}
            placeholder="Maharashtra"
          />
          {errors.state && <p className="text-xs text-red-500 mt-1">{errors.state.message}</p>}
        </div>
        <div>
          <label className="label">Pincode *</label>
          <input
            {...register("pincode", {
              required: "Pincode is required",
              pattern: { value: /^[0-9]{6}$/, message: "Enter a valid 6-digit pincode" },
            })}
            className={`input bg-white ${errors.pincode ? "border-red-400" : ""}`}
            placeholder="400001"
          />
          {errors.pincode && <p className="text-xs text-red-500 mt-1">{errors.pincode.message}</p>}
        </div>
        <div>
          <label className="label">Landmark (optional)</label>
          <input {...register("landmark")} className="input bg-white" placeholder="Near park, etc." />
        </div>
      </div>
      <div className="flex items-center gap-3 pt-1">
        <button
          type="submit"
          disabled={saving}
          className="btn-primary flex items-center justify-center gap-2 text-sm px-4 py-2"
        >
          {saving ? <><Spinner size="sm" /> Saving...</> : "Save Address"}
        </button>
        <button type="button" onClick={onCancel} className="text-sm text-gray-500 hover:text-gray-700">
          Cancel
        </button>
      </div>
    </form>
  );
}